// Sample Airbnb income for the video-demo channel (FILM MODE only, see
// demoMode.ts isFilmMode): the live app's Airbnb section — monthly payout,
// occupancy, fees and each stay — filled with invented figures on two demo
// units, so product films show the feature exactly as owners see it.
//
// Never real data, and never shown outside film mode: real Airbnb figures come
// from DreamHaven's feed for a signed-in owner (Backend/airbnbIncomeRoutes.js).
// Figures follow the live arithmetic: payout after commission, minus cleaning,
// Tourism Dirham and a 20 % management fee on the remainder = net to owner.

interface Spec { unitId: string; name: string; apartment: string; listingUrl: string; adr: number; occ: number[]; cleaning: number }

const SPECS: Spec[] = [
  { unitId: 'demo-unit-creek-vistas', name: 'Creek Vistas Heights', apartment: 'CVH-1204', listingUrl: 'https://www.airbnb.com/', adr: 640, occ: [0.62, 0.71, 0.78, 0.84, 0.88, 0.74], cleaning: 180 },
  { unitId: 'demo-unit-wynwood', name: 'Wynwood Residences', apartment: 'WYN-905', listingUrl: 'https://www.airbnb.com/', adr: 920, occ: [0.58, 0.66, 0.72, 0.81, 0.86, 0.69], cleaning: 240 },
];

const ym = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const daysIn = (y: number, m: number) => new Date(y, m + 1, 0).getDate();

function monthsFor(spec: Spec) {
  const now = new Date();
  const out: any[] = [];
  for (let back = 0; back < 6; back++) {
    const d = new Date(now.getFullYear(), now.getMonth() - back, 1);
    const avail = back === 0 ? now.getDate() : daysIn(d.getFullYear(), d.getMonth());
    const occ = spec.occ[5 - back] ?? 0.7;
    const nights = Math.max(1, Math.round(avail * occ));
    const bookings = Math.max(1, Math.round(nights / 4.2));
    const gross = Math.round(nights * spec.adr * (0.94 + 0.12 * ((back * 7) % 5) / 5));
    const cleaning = bookings * spec.cleaning;
    const tourismDirham = nights * 10;
    const managementFee = Math.round((gross - cleaning - tourismDirham) * 0.2);
    out.push({
      month: ym(d), gross, cleaning, tourismDirham, managementFee,
      net: gross - cleaning - tourismDirham - managementFee,
      bookedNights: nights, availableNights: avail, bookings, currency: 'AED',
      ownerPayoutDate: back === 0 ? null : `${ym(new Date(d.getFullYear(), d.getMonth() + 1, 1))}-10`,
      status: back === 0 ? 'provisional' : 'final',
    });
  }
  return out; // newest first
}

/** The payload useAirbnbIncome returns, for whichever sample units are on screen. */
export function filmAirbnbPayload(unitIds: string[]) {
  const ids = new Set(unitIds);
  return {
    lastSyncAt: new Date().toISOString(),
    units: SPECS.filter((s) => ids.has(s.unitId)).map((s) => ({
      unitId: s.unitId, name: s.name, apartment: s.apartment, manager: 'DreamHaven', listingUrl: s.listingUrl, months: monthsFor(s),
    })),
  };
}

/** Individual stays for the bookings card (newest first), matching the months above. */
export function filmAirbnbBookings(unitId: string) {
  const spec = SPECS.find((s) => s.unitId === unitId);
  if (!spec) return [];
  const channels = ['airbnb', 'airbnb', 'booking_com', 'airbnb', 'direct'];
  const rows: any[] = [];
  let cursor = new Date();
  cursor.setDate(cursor.getDate() - 2);
  for (let i = 0; i < 14; i++) {
    const nights = [3, 5, 2, 4, 6, 3, 7][i % 7];
    const checkOut = new Date(cursor);
    const checkIn = new Date(cursor);
    checkIn.setDate(checkIn.getDate() - nights);
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    rows.push({
      bookingId: `demo-${unitId.slice(10, 14)}-${i}`, checkIn: iso(checkIn), checkOut: iso(checkOut), nights,
      guests: 1 + (i % 4), channel: channels[i % channels.length], payoutAed: Math.round(nights * spec.adr * (0.95 + (i % 3) * 0.05)),
    });
    cursor = new Date(checkIn);
    cursor.setDate(cursor.getDate() - (1 + (i % 3)));
  }
  return rows;
}
